import { useState } from "react";
import { useLocation } from "wouter";
import {
  useListRecords, useDeleteRecord, useListStudents, useListClasses, useListCourses,
  getListRecordsQueryKey,
  type ListRecordsParams, type ListRecordsType, type ListRecordsEntered,
} from "@workspace/api-client-react";
import { useMarkRecordEntered } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Plus, Search, Trash2, Pencil, FileText, NotebookPen, AlertTriangle,
  ClipboardList, CheckCircle2, Circle, X, ChevronDown, ChevronUp,
} from "lucide-react";
import { RecordPreviewPanel } from "@/components/record-preview-panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";
import { useRegion } from "@/contexts/RegionContext";
import { SchoolBadge } from "@/components/school-badge";
import { formatStudentName, useDefaultStudentDisplayMode, type Student as DisplayStudent } from "@/lib/studentDisplay";

// Read a query string param without depending on a router-specific hook so
// this works regardless of whether the page mounted with or without an
// initial search string in the URL.
function readSearchParam(name: string): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(name);
}

export default function Records() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { terms } = useRegion();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  const [filterEntered, setFilterEntered] = useState<string>("all"); // all | not_entered | entered
  // ?studentId=123 lets other surfaces (e.g. the merge wizard's "View"
  // links) deep-link into a pre-filtered records list. Captured once at
  // mount; clearing the chip below removes it without touching the URL.
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [studentIdFilter, setStudentIdFilter] = useState<number | null>(() => {
    const raw = readSearchParam("studentId");
    if (!raw) return null;
    const n = parseInt(raw);
    return Number.isFinite(n) ? n : null;
  });

  const RECORD_TYPES = [
    { value: "nccd",      label: terms.adjustmentType,   color: "bg-blue-100 text-blue-800",     icon: ClipboardList },
    { value: "contact",   label: "Contact",              color: "bg-green-100 text-green-800",   icon: NotebookPen },
    { value: "behaviour", label: terms.behaviourSpelling, color: "bg-purple-100 text-purple-800", icon: AlertTriangle },
    { value: "general",   label: "General",              color: "bg-gray-100 text-gray-800",     icon: FileText },
  ];

  function recordTypeStyle(type: string) {
    return RECORD_TYPES.find((r) => r.value === type) ?? RECORD_TYPES[3];
  }

  const params: ListRecordsParams = {
    ...(filterType !== "all" ? { type: filterType as ListRecordsType } : {}),
    ...(filterEntered === "entered"
      ? { entered: "true" as ListRecordsEntered }
      : filterEntered === "not_entered"
        ? { entered: "false" as ListRecordsEntered }
        : {}),
    ...(search ? { search } : {}),
    ...(studentIdFilter !== null ? { studentId: studentIdFilter } : {}),
  };

  const { data: records, isLoading } = useListRecords(params);
  const { data: students } = useListStudents();
  // Include archived classes so historical records that reference a
  // year-end-archived class still resolve to a name (with an "(archived)"
  // suffix added below). The active record-new picker uses the default
  // useListClasses() and never sees archived rows.
  const { data: classes } = useListClasses({ includeArchived: "1" });
  // Same pattern for courses — historical records that reference an
  // archived course should still resolve to a name with an "(archived)"
  // suffix.
  const { data: courses } = useListCourses({ includeArchived: "1" });
  const deleteRecord = useDeleteRecord();
  const markEntered = useMarkRecordEntered();

  const accountDisplayMode = useDefaultStudentDisplayMode();
  const studentMap = new Map(
    students?.map((s) => [s.id, formatStudentName(s as DisplayStudent, accountDisplayMode)]),
  );
  const studentSchoolMap = new Map(students?.map((s) => [s.id, s.schoolId != null]));
  const classMap = new Map(
    classes?.map((c) => [c.id, c.archived ? `${c.name} (archived)` : c.name]),
  );
  const classSchoolMap = new Map(classes?.map((c) => [c.id, c.schoolId != null]));
  const courseMap = new Map(
    courses?.map((c) => [c.id, c.archived ? `${c.name} (archived)` : c.name]),
  );

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this record?")) return;
    await deleteRecord.mutateAsync({ recordId: id });
    queryClient.invalidateQueries({ queryKey: getListRecordsQueryKey() });
  };

  const handleToggleEntered = async (id: number, current: boolean) => {
    try {
      await markEntered.mutateAsync({ recordId: id, entered: !current });
      queryClient.invalidateQueries({ queryKey: getListRecordsQueryKey() });
      toast({
        title: !current ? "Marked as uploaded to school system" : "Marked as not uploaded",
        description: !current ? "This record has been flagged as transferred." : undefined,
      });
    } catch (err) {
      toast({
        title: "Couldn't update the record",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const notEnteredCount = records?.filter((r) => !(r as any).enteredIntoSentral).length ?? 0;
  const filteredStudentName =
    studentIdFilter !== null
      ? students?.find((s) => s.id === studentIdFilter)
      : null;
  const filteredStudentLabel = filteredStudentName
    ? formatStudentName(filteredStudentName as DisplayStudent, accountDisplayMode)
    : studentIdFilter !== null
    ? "Hidden student"
    : null;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">Records</h1>
          {notEnteredCount > 0 && filterEntered !== "entered" && (
            <p className="text-sm text-amber-600 mt-0.5">
              {notEnteredCount} record{notEnteredCount !== 1 ? "s" : ""} not yet uploaded to your school system
            </p>
          )}
        </div>
        <Button
          className="bg-pink-500 hover:bg-pink-600 text-white rounded-full h-11 px-5 shadow-md shadow-pink-500/20 flex-shrink-0"
          onClick={() => setLocation("/app/records/new")}
        >
          <Plus className="h-4 w-4 mr-2" />
          New Record
        </Button>
      </div>

      {/* Active student filter chip — set when this page was opened with
          a ?studentId=… deep link (e.g. from the merge wizard's "View"
          links). Clearing it removes the filter so the user sees all
          records again. */}
      {studentIdFilter !== null && (
        <div className="mb-4 flex items-center gap-2" data-testid="filter-chip-student">
          <span className="inline-flex items-center gap-1.5 text-sm bg-blue-50 border border-blue-200 text-blue-800 rounded-full pl-3 pr-1 py-1">
            Showing records for <strong className="font-semibold">{filteredStudentLabel}</strong>
            <button
              type="button"
              onClick={() => setStudentIdFilter(null)}
              className="ml-1 inline-flex items-center justify-center h-5 w-5 rounded-full hover:bg-blue-100"
              aria-label="Clear student filter"
              data-testid="button-clear-student-filter"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Search records…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Record type filter */}
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {RECORD_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Upload status filter */}
        <Select value={filterEntered} onValueChange={setFilterEntered}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Upload status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All records</SelectItem>
            <SelectItem value="not_entered">Not yet uploaded</SelectItem>
            <SelectItem value="entered">Uploaded to system</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-100 animate-pulse rounded-xl" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && (!records || records.length === 0) && (
        <div className="py-24 text-center border-2 border-dashed border-gray-200 rounded-xl">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No records found</h3>
          <p className="text-gray-500 mb-6">
            {filterEntered === "entered"
              ? "No records have been marked as uploaded to your school system yet."
              : filterEntered === "not_entered"
              ? "All records have been uploaded to your school system."
              : "Start documenting student interactions and adjustments."}
          </p>
          {filterEntered === "all" && filterType === "all" && !search && (
            <Button onClick={() => setLocation("/app/records/new")} data-write-action>Create First Record</Button>
          )}
        </div>
      )}

      {/* Records list */}
      <div className="space-y-2">
        {records?.map((record) => {
          const typeInfo = recordTypeStyle(record.type);
          const TypeIcon = typeInfo.icon;
          const studentNames: string[] = (record as any).studentNames ?? record.studentIds.map(
            (id) => studentMap.get(id) ?? "Hidden student"
          );
          const className = record.classId ? classMap.get(record.classId) : null;
          const courseName = record.courseId ? courseMap.get(record.courseId) : null;
          const classFromSchool = record.classId ? !!classSchoolMap.get(record.classId) : false;
          const studentFromSchool = record.studentIds.some((id) => studentSchoolMap.get(id));
          const showSchoolBadge = classFromSchool || studentFromSchool;
          const entered = (record as any).enteredIntoSentral as boolean;

          const isExpanded = expandedId === record.id;

          return (
            <div
              key={record.id}
              className={`bg-white border rounded-xl overflow-hidden hover:shadow-sm transition-all group cursor-pointer ${
                entered ? "border-green-200 bg-green-50/30" : "border-gray-200"
              }`}
              onClick={() => setExpandedId(isExpanded ? null : record.id)}
            >
              {/* ── Collapsed row ──────────────────────────────────────── */}
              <div className="p-4 flex items-start gap-4">
                {/* Type icon */}
                <div className="flex-shrink-0 mt-0.5">
                  <div className={`inline-flex items-center rounded-lg p-2 ${typeInfo.color}`}>
                    <TypeIcon className="h-4 w-4" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge className={`text-xs font-medium border-0 ${typeInfo.color}`}>
                      {typeInfo.label}
                    </Badge>
                    {className && (
                      <Badge variant="secondary" className="text-xs">{className}</Badge>
                    )}
                    {courseName && (
                      <Badge variant="outline" className="text-xs">{courseName}</Badge>
                    )}
                    {/* Task #142: server-side enrich-on-read flag — true when the
                        record stores a courseId/classId but the referenced row
                        no longer exists. Render a muted tag instead of silently
                        dropping the badge so head teachers/reviewers can see
                        the broken reference. */}
                    {!courseName && record.courseMissing && (
                      <Badge
                        variant="outline"
                        className="text-xs text-red-600 border-red-200 bg-red-50"
                        data-testid="record-deleted-course"
                        title="The course this record pointed at no longer exists."
                      >
                        (deleted course)
                      </Badge>
                    )}
                    {!className && record.classMissing && (
                      <Badge
                        variant="outline"
                        className="text-xs text-red-600 border-red-200 bg-red-50"
                        data-testid="record-deleted-class"
                        title="The class this record pointed at no longer exists."
                      >
                        (deleted class)
                      </Badge>
                    )}
                    {showSchoolBadge && <SchoolBadge />}
                    {entered && (
                      <span className="flex items-center gap-1 text-xs text-green-700 font-medium">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Uploaded to system
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-gray-800 line-clamp-2">
                    {record.description || record.shorthandNotes || "No description"}
                  </p>

                  <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                    <span>{format(new Date(record.date), "d MMM yyyy")}</span>
                    {record.period && <span>Period {record.period}</span>}
                    {studentNames.length > 0 && (
                      <span className="text-blue-600 font-medium">
                        {studentNames.length === 1
                          ? studentNames[0]
                          : `${studentNames[0]} +${studentNames.length - 1} more`}
                      </span>
                    )}
                    <span className={`flex items-center gap-0.5 ml-auto transition-colors ${isExpanded ? "text-gray-500" : "text-gray-300 group-hover:text-gray-400"}`}>
                      {isExpanded
                        ? <ChevronUp className="h-3.5 w-3.5" />
                        : <ChevronDown className="h-3.5 w-3.5" />}
                      <span className="text-[10px] font-medium">
                        {isExpanded ? "Collapse" : "Preview"}
                      </span>
                    </span>
                  </div>
                </div>

                {/* Actions — stop propagation so clicks here don't toggle the preview */}
                <div
                  className="flex items-center gap-1 flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Enter into Sentral toggle — always visible */}
                  <button
                    title={entered ? "Mark as not uploaded" : "Mark as uploaded to school system"}
                    onClick={() => handleToggleEntered(record.id, entered)}
                    className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-all ${
                      entered
                        ? "border-green-300 bg-green-50 text-green-700 hover:bg-green-100"
                        : "border-gray-200 bg-white text-gray-500 hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50"
                    }`}
                  >
                    {entered ? (
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    ) : (
                      <Circle className="h-3.5 w-3.5" />
                    )}
                    {entered ? "Uploaded" : "Mark as uploaded"}
                  </button>

                  {/* Edit + Delete — appear on hover */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-blue-600"
                      onClick={() => setLocation(`/app/records/new?edit=${record.id}`)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-600"
                      onClick={() => handleDelete(record.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* ── Expanded preview ───────────────────────────────────── */}
              {isExpanded && (
                <RecordPreviewPanel
                  record={record as any}
                  enteredIntoSentral={(record as any).enteredIntoSentral ?? false}
                  studentNames={studentNames}
                  className={className ?? null}
                  courseName={courseName ?? null}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
